/**
 * AssignContractPanel
 *
 * Contenido de asignación de contrato sin wrapper <Dialog>.
 * Reutilizable en tabs o cualquier contenedor.
 * La lógica es idéntica a AssignContractModal — solo difiere en la estructura JSX:
 * - Sin DialogTitle/DialogContent/DialogActions
 * - Sin selector de booking (bookingId llega por prop directamente)
 * - Auto-simulación al montar / al cambiar bookingId (no depende de prop `open`)
 */
import { useState, useCallback, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Skeleton,
} from "@mui/material";
import {
  CheckCircleOutlined as CheckCircle,
  WarningAmberOutlined as Warning,
  InfoOutlined as Info,
  PlayArrowOutlined as PlayArrow,
  PreviewOutlined as Preview,
} from "@mui/icons-material";
import { useFetch } from "../../hooks/use-fetch-data";

export interface AssignContractPanelProps {
  bookingId: number;
  bookingReference: string | null;
  operationType: string | null;
  existingContractSq?: string | null;
  onAssignmentComplete?: () => void;
}

interface EquipmentAssignment {
  bookingEquipmentId: number;
  equipmentId: number;
  equipmentCode: string;
  containerId?: string | null;
  previousContractLineId: number | null;
  newContractLineId: number | null;
  contractNumber: string | null;
  reason: string;
  wasUpdated: boolean;
}

interface AssignmentResult {
  bookingId: number;
  bookingReference: string;
  operationType: string;
  totalEquipments: number;
  equipmentsAssigned: number;
  equipmentsWithoutContract: number;
  status: string;
  equipmentAssignments: EquipmentAssignment[];
  previousContractLineId?: number | null;
  newContractLineId?: number | null;
  contractNumber?: string | null;
  reason?: string;
  wasUpdated?: boolean;
}

interface AssignmentResponse {
  totalBookingsProcessed: number;
  totalEquipmentsProcessed: number;
  totalEquipmentsUpdated: number;
  totalEquipmentsSkipped: number;
  totalEquipmentsUnchanged: number;
  isPreview: boolean;
  assignments: AssignmentResult[];
  totalProcessed?: number;
  totalUpdated?: number;
  totalSkipped?: number;
  totalUnchanged?: number;
}

export function AssignContractPanel({
  bookingId,
  bookingReference,
  operationType,
  existingContractSq,
  onAssignmentComplete,
}: AssignContractPanelProps) {
  const { fetchData } = useFetch();

  const [contractSq, setContractSq] = useState<string>("");
  const [skipExportValidation, setSkipExportValidation] = useState<boolean>(false);
  const [forceReassign, setForceReassign] = useState<boolean>(false);

  const [simulating, setSimulating] = useState<boolean>(false);
  const [previewResult, setPreviewResult] = useState<AssignmentResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [executing, setExecuting] = useState<boolean>(false);
  const [executionResult, setExecutionResult] = useState<AssignmentResponse | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);

  const simulationRef = useRef<number>(0);

  const isExport = operationType === "Export" || operationType === "E";

  const runSimulation = useCallback(async (sqValue: string, skipExport: boolean, currentForceReassign: boolean) => {
    const isExportType = operationType === "E" || operationType === "Export";
    if (isExportType && !sqValue.trim() && !skipExport) return;

    const currentSimulation = ++simulationRef.current;
    setSimulating(true);
    setPreviewError(null);
    setPreviewResult(null);

    try {
      const payload: Record<string, unknown> = {
        bookingId,
        includeDetails: true,
        previewOnly: true,
      };

      if (sqValue.trim()) payload.contractSq = sqValue.trim();
      if (skipExport) payload.skipExportValidation = true;
      if (currentForceReassign) payload.forceReassign = true;

      const { result: apiResult, error: fetchError } = await fetchData("bookings/assign-contract-lines", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (currentSimulation !== simulationRef.current) return;

      if (fetchError) {
        setPreviewError(`Error al simular: ${fetchError}`);
        return;
      }

      if (!apiResult?.success) {
        setPreviewError(apiResult?.message || "Error desconocido al simular");
        return;
      }

      const resultData = apiResult.data;
      if (apiResult.message && resultData) {
        resultData._message = apiResult.message;
      }
      setPreviewResult(resultData);
    } catch {
      if (currentSimulation === simulationRef.current) {
        setPreviewError("Error inesperado al simular asignación");
      }
    } finally {
      if (currentSimulation === simulationRef.current) {
        setSimulating(false);
      }
    }
  }, [bookingId, operationType, fetchData]);

  // Auto-simulación al montar o cambiar bookingId
  useEffect(() => {
    const sq = existingContractSq || "";
    setContractSq(sq);
    setSkipExportValidation(false);
    setForceReassign(false);
    setPreviewResult(null);
    setPreviewError(null);
    setExecutionResult(null);
    setExecutionError(null);
    runSimulation(sq, false, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const handleContractSqChange = (value: string) => {
    setContractSq(value);
    setExecutionResult(null);
    setExecutionError(null);
  };

  const handleSkipExportChange = (checked: boolean) => {
    setSkipExportValidation(checked);
    setExecutionResult(null);
    setExecutionError(null);
    runSimulation(contractSq, checked, forceReassign);
  };

  const handleForceReassignChange = (checked: boolean) => {
    setForceReassign(checked);
    setExecutionResult(null);
    setExecutionError(null);
    runSimulation(contractSq, skipExportValidation, checked);
  };

  const handleExecute = useCallback(async () => {
    const isExportType = operationType === "E" || operationType === "Export";
    if (isExportType && !contractSq.trim() && !skipExportValidation) {
      setExecutionError("Para operaciones de exportación, debes ingresar un Contract SQ o marcar la opción para saltar validación");
      return;
    }

    setExecuting(true);
    setExecutionError(null);
    setExecutionResult(null);

    try {
      const payload: Record<string, unknown> = {
        bookingId,
        includeDetails: true,
        previewOnly: false,
      };

      if (contractSq.trim()) payload.contractSq = contractSq.trim();
      if (skipExportValidation) payload.skipExportValidation = true;
      if (forceReassign) payload.forceReassign = true;

      const { result: apiResult, error: fetchError } = await fetchData("bookings/assign-contract-lines", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (fetchError) {
        setExecutionError(`Error al asignar contrato: ${fetchError}`);
        return;
      }

      if (!apiResult?.success) {
        setExecutionError(apiResult?.message || "Error desconocido al asignar contrato");
        return;
      }

      const resultData = apiResult.data;
      if (apiResult.message && resultData) {
        resultData._message = apiResult.message;
      }
      setExecutionResult(resultData);
      setPreviewResult(null);

      const totalUpdated = apiResult.data?.totalEquipmentsUpdated ?? apiResult.data?.totalUpdated ?? 0;
      if (totalUpdated > 0) {
        onAssignmentComplete?.();
      }
    } catch {
      setExecutionError("Error inesperado al asignar contrato");
    } finally {
      setExecuting(false);
    }
  }, [bookingId, operationType, contractSq, skipExportValidation, forceReassign, fetchData, onAssignmentComplete]);

  const previewHasChanges = previewResult &&
    ((previewResult.totalEquipmentsUpdated ?? previewResult.totalUpdated ?? 0) > 0);

  const executionWasSuccessful = executionResult &&
    ((executionResult.totalEquipmentsUpdated ?? executionResult.totalUpdated ?? 0) > 0);

  const renderAssignmentResult = (result: AssignmentResponse, isPreviewMode: boolean) => {
    const assignment = result.assignments?.[0];
    const backendMessage = (result as any)._message as string | undefined;

    if (!assignment) {
      if (backendMessage) {
        return (
          <Alert severity="info" sx={{ mt: 2 }}>
            {backendMessage}
          </Alert>
        );
      }
      return null;
    }

    const equipmentAssignments = assignment.equipmentAssignments || [];
    const hasEquipmentAssignments = equipmentAssignments.length > 0;

    const totalUpdated = result.totalEquipmentsUpdated ?? result.totalUpdated ?? 0;
    const totalUnchanged = result.totalEquipmentsUnchanged ?? result.totalUnchanged ?? 0;

    const isSuccess = totalUpdated > 0;
    const isUnchanged = totalUnchanged > 0 && totalUpdated === 0;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mt: 2,
          border: 1,
          borderColor: isPreviewMode
            ? (isSuccess ? "primary.main" : isUnchanged ? "info.main" : "warning.main")
            : (isSuccess ? "success.main" : isUnchanged ? "info.main" : "warning.main"),
          borderRadius: 2,
          bgcolor: isPreviewMode
            ? (isSuccess ? "primary.50" : isUnchanged ? "info.lighter" : "warning.lighter")
            : (isSuccess ? "success.lighter" : isUnchanged ? "info.lighter" : "warning.lighter"),
        }}
      >
        {isPreviewMode && (
          <Chip
            icon={<Preview />}
            label="Vista previa"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mb: 1.5 }}
          />
        )}
        {!isPreviewMode && isSuccess && (
          <Chip
            icon={<CheckCircle />}
            label="Ejecutado"
            size="small"
            color="success"
            sx={{ mb: 1.5 }}
          />
        )}

        {(result as any)._message && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5, fontStyle: "italic" }}>
            {(result as any)._message}
          </Typography>
        )}

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          {isSuccess ? (
            isPreviewMode ? <Preview color="primary" sx={{ mt: 0.5 }} /> : <CheckCircle color="success" sx={{ mt: 0.5 }} />
          ) : isUnchanged ? (
            <Info color="info" sx={{ mt: 0.5 }} />
          ) : (
            <Warning color="warning" sx={{ mt: 0.5 }} />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="600">
              {isPreviewMode
                ? (isSuccess
                  ? "Se asignará contrato a este booking"
                  : isUnchanged
                  ? "No se requieren cambios"
                  : "No se encontró contrato compatible")
                : (isSuccess
                  ? "Contrato asignado exitosamente"
                  : isUnchanged
                  ? "Sin cambios necesarios"
                  : "No se encontró contrato compatible")
              }
            </Typography>

            {assignment.status && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>{assignment.status}</Typography>
            )}

            {hasEquipmentAssignments && equipmentAssignments.map((eq, idx) => (
              <Box key={eq.bookingEquipmentId || idx} sx={{ mt: 1.5, p: 1.5, bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Chip
                    label={eq.equipmentCode}
                    size="small"
                    color={eq.wasUpdated ? (isPreviewMode ? "primary" : "success") : "default"}
                    variant={eq.wasUpdated ? "filled" : "outlined"}
                  />
                  {eq.containerId && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                      {eq.containerId}
                    </Typography>
                  )}
                </Box>

                {eq.contractNumber && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Contrato:</strong> {eq.contractNumber}
                    {eq.newContractLineId && ` (Línea ${eq.newContractLineId})`}
                  </Typography>
                )}

                {eq.previousContractLineId && eq.previousContractLineId !== eq.newContractLineId && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Anterior:</strong> Línea {eq.previousContractLineId}
                  </Typography>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Razón:</strong> {eq.reason}
                </Typography>
              </Box>
            ))}

            {!hasEquipmentAssignments && (
              <>
                {assignment.newContractLineId && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Contrato:</strong> {assignment.contractNumber || "N/A"} (Línea {assignment.newContractLineId})
                  </Typography>
                )}

                {assignment.previousContractLineId && assignment.previousContractLineId !== assignment.newContractLineId && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Anterior:</strong> Línea {assignment.previousContractLineId}
                  </Typography>
                )}

                {assignment.reason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <strong>Razón:</strong> {assignment.reason}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderSimulationLoading = () => (
    <Paper
      elevation={0}
      sx={{ p: 2, mt: 2, border: 1, borderColor: "divider", borderRadius: 2 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="subtitle2" color="text.secondary">
          Buscando datos...
        </Typography>
      </Box>
      <Skeleton variant="rounded" height={24} width="60%" sx={{ mb: 1 }} />
      <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
      <Skeleton variant="rounded" height={16} width="40%" />
    </Paper>
  );

  const anyLoading = simulating || executing;

  return (
    <Box>
      {/* Información del booking */}
      <Paper
        variant="outlined"
        sx={{ mb: 3, p: 2, bgcolor: "primary.50", borderColor: "primary.200", borderRadius: 2 }}
      >
        <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1 }}>
          Booking
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" fontWeight="600">
            {bookingReference || `#${bookingId}`}
          </Typography>
          <Chip
            label={
              operationType === "E" || operationType === "Export"
                ? "Exportación"
                : operationType === "I" || operationType === "Import"
                ? "Importación"
                : operationType || "—"
            }
            size="small"
            color={isExport ? "warning" : "info"}
          />
          {existingContractSq && (
            <Chip
              label={`Contrato actual: ${existingContractSq}`}
              size="small"
              color="default"
              variant="outlined"
            />
          )}
        </Box>
      </Paper>

      {/* Contract SQ para Export */}
      {isExport && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Contract SQ (Requerido para Exportación)
          </Typography>
          <TextField
            label="Número de Contrato (SQ)"
            value={contractSq}
            onChange={(e) => handleContractSqChange(e.target.value)}
            size="small"
            fullWidth
            placeholder="Ej: SQ-2024-MSC-001"
            disabled={anyLoading}
            helperText="Ingresa el SQ del contrato para buscar la línea correspondiente"
            error={isExport && !contractSq && !skipExportValidation}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={skipExportValidation}
                onChange={(e) => handleSkipExportChange(e.target.checked)}
                size="small"
                disabled={anyLoading}
              />
            }
            label={<Typography variant="caption" color="text.secondary">Permitir asignación sin Contract SQ</Typography>}
            sx={{ mt: 0.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={forceReassign}
                onChange={(e) => handleForceReassignChange(e.target.checked)}
                size="small"
                disabled={anyLoading}
              />
            }
            label={<Typography variant="caption" color="text.secondary">Forzar reasignación (reevaluar equipos ya asignados)</Typography>}
            sx={{ mt: 0.5 }}
          />
        </Box>
      )}

      {/* Contract SQ opcional para Import */}
      {!isExport && (
        <Box sx={{ mb: 3 }}>
          <TextField
            label="Contract SQ (Opcional)"
            value={contractSq}
            onChange={(e) => handleContractSqChange(e.target.value)}
            size="small"
            fullWidth
            placeholder="Dejar vacío para búsqueda automática"
            disabled={anyLoading}
            helperText="Si se especifica, se buscará ese contrato específico"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={forceReassign}
                onChange={(e) => handleForceReassignChange(e.target.checked)}
                size="small"
                disabled={anyLoading}
              />
            }
            label={<Typography variant="caption" color="text.secondary">Forzar reasignación (reevaluar equipos ya asignados)</Typography>}
            sx={{ mt: 0.5 }}
          />
        </Box>
      )}

      {previewError && !executionResult && (
        <Alert severity="warning" sx={{ mb: 2 }}>{previewError}</Alert>
      )}
      {executionError && (
        <Alert severity="error" sx={{ mb: 2 }}>{executionError}</Alert>
      )}

      {simulating && renderSimulationLoading()}
      {!simulating && previewResult && !executionResult && renderAssignmentResult(previewResult, true)}
      {executionResult && renderAssignmentResult(executionResult, false)}

      {/* Acciones */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        {executionWasSuccessful ? (
          <Chip icon={<CheckCircle />} label="Asignación completada" color="success" />
        ) : (
          <Button
            variant="contained"
            onClick={handleExecute}
            disabled={anyLoading || (isExport && !contractSq.trim() && !skipExportValidation)}
            startIcon={executing ? <CircularProgress size={18} /> : <PlayArrow />}
            color={previewHasChanges ? "success" : "primary"}
          >
            {executing ? "Ejecutando..." : previewHasChanges ? "Ejecutar Asignación" : "Asignar Contrato"}
          </Button>
        )}
      </Box>
    </Box>
  );
}
